import { Button } from '@studio/design-system/button';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import { Sel, STagNew } from '#~/stag/tag.js';
import { openBridgeFailureChat, openBridgeHelpArticle } from '../help-actions.js';
import type { BridgeConnectionSteps } from '../types.js';
import { BridgeConnectionStepsIndicator } from './connection-steps.js';
import { FunnelShell } from './funnel-shell.js';
import { useStepView } from '../hooks/use-step-view.js';
import { onboardingContextFor, useOnboardingTracker } from '../hooks/use-onboarding-tracker.js';

interface BridgeFailureStateProps {
  /** One-based Bridge readiness attempt that reached this timeout. */
  readonly attemptNumber: number;
  readonly onRetry: () => void;
  readonly steps: BridgeConnectionSteps;
  /** Escape / the close affordance: dismiss WITHOUT recording the permanent
   *  skip. Distinct from onSkip, which is an explicit user decision to defer —
   *  see gate.tsx handleDismiss (ENG-3796 live-run defect 1). */
  readonly onDismiss: () => void;
}

// Failure screen shown when the bridge readiness ceiling elapses. Copy
// adapted from RFD 0006's "Detection failed" prototype. Recovery order:
// Intercom CTA first (primary), retry second, help article below.
//
// No "Skip": a first-time user without a bridge cannot use the product, so this
// screen does not offer to record that they are done. It DOES offer a way out —
// onDismiss, same as every other screen in the funnel. The earlier version had
// neither, which trapped exactly the user most likely to be stuck, and is the
// same defect Didier's ITB pass found on the connecting screen (funnel-detecting
// carries the matching note).
export const BridgeFailureState = ({
  attemptNumber,
  onRetry,
  steps,
  onDismiss
}: BridgeFailureStateProps): ReactElement => {
  useStepView('bridge-failure');
  const tracker = useOnboardingTracker();
  const reportedRef = useRef(false);

  useEffect(() => {
    if (reportedRef.current) return;
    reportedRef.current = true;
    const reason = !steps.agentDetected
      ? 'bridge_not_detected'
      : !steps.bridgeAuthenticated
        ? 'cloud_connection_failed'
        : 'studio_link_failed';

    tracker.trackFailed({
      context: onboardingContextFor('bridge-failure'),
      rawError: 'Bridge did not connect within the readiness window.',
      terminal: true,
      properties: {
        reason,
        attempt_number: attemptNumber,
        agent_detected: steps.agentDetected,
        bridge_authenticated: steps.bridgeAuthenticated,
        studio_linked: steps.roomLinked
      }
    });
  }, [attemptNumber, steps, tracker]);

  return (
    <FunnelShell announceTitle="Couldn't connect your Bridge" onEscape={onDismiss}>
      <ErrorBadge />

      <h1 className='mt-16 text-2xl font-demibold text-white'>We couldn't connect your Bridge.</h1>

      <p className='mt-12 max-w-[28rem] text-md text-foreground/75'>
        Connection to {vendorName()} Cloud couldn't be established. This usually means a firewall is
        blocking the connection, or your network needs a moment to settle.
      </p>

      <div className='mt-12 w-full max-w-[28rem]'>
        <BridgeConnectionStepsIndicator steps={steps} errored />
      </div>

      <p className='mt-12 max-w-[28rem] text-sm text-secondary'>
        Our support team can walk you through it.
      </p>

      <div className='mt-24 flex flex-col gap-12 dialog:flex-row'>
        <Button
          className='text-foreground'
          {...STagNew(Sel.OnboardingFailure, 'retry')}
          onClick={onRetry}
          size='lg'
          variant='secondary'
        >
          Try Again
        </Button>
        {/* Get Help closes the funnel as well as opening the messenger. The
            Intercom widget mounts into the page BEHIND this overlay, so leaving
            the funnel up hides the very thing the button just opened (reported
            live 2026-08-25). Dismiss, not skip: asking support for help is not
            a decision to be done with onboarding. */}
        <Button
          autoFocus
          {...STagNew(Sel.OnboardingFailure, 'get_help')}
          onClick={() => {
            openBridgeFailureChat();
            onDismiss();
          }}
          size='lg'
          variant='primary'
        >
          Get Help
        </Button>
      </div>

      <Button
        className='mt-12'
        {...STagNew(Sel.OnboardingFailure, 'learn_more')}
        onClick={openBridgeHelpArticle}
        size='sm'
        variant='tertiary'
      >
        What is the {vendorName()} Bridge?
      </Button>
    </FunnelShell>
  );
};

const ErrorBadge = (): ReactElement => {
  return (
    <div
      aria-hidden='true'
      className='bg-error/20 flex h-64 w-64 items-center justify-center rounded-full'
    >
      <svg viewBox='0 0 32 32' className='h-32 w-32 text-error' fill='none'>
        <path
          d='M11 11L21 21M21 11L11 21'
          stroke='currentColor'
          strokeWidth='2.5'
          strokeLinecap='round'
        />
      </svg>
    </div>
  );
};
